<script setup lang="ts">
import { ref } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import jwtService from '@/services/jwtService.ts'
import httpClient from '@/services/http/httpClient.ts'
import emitter from '@/services/emitter.ts'
import { jwtDecode } from 'jwt-decode'
import userAuthorizationService from '@/services/userAuthorizationService.ts'
import AppLayout from '@/layouts/AppLayout.vue'
import { Roles } from './constants'
import type { JwtPayload } from '@/types/auth'

const router = useRouter()
const isAuthorized = ref(false)
const currentUserRole = ref<Roles | null>(null)

// При старте (перезагрузка/возврат) — если токен есть, запускаем проактивное обновление,
// чтобы навигация не упиралась в 401 на первом же клике.
if (jwtService.getToken()) {
  httpClient.scheduleProactiveRefresh()
}

const showLayout = () => router.currentRoute.value.path !== '/login' && isAuthorized.value

// Роль в JWT может прийти строкой или массивом — нормализуем к набору ролей.
function extractRoles(payload: JwtPayload): Roles[] {
  const raw = payload.role
  if (raw == null) return []
  return Array.isArray(raw) ? raw : [raw]
}

router.beforeEach((to, from, next) => {
  const token = jwtService.getToken()

  // Страница входа: неавторизованному показываем форму; уже вошедшего НЕ держим на логине, а ведём
  // по returnUrl (или на стартовый экран «Аналитика»), чтобы открытый вручную/по ссылке /login не
  // показывал форму залогиненному сотруднику.
  if (to.path === '/login') {
    if (!token) {
      next()
      return
    }
    const raw = to.query.returnUrl
    const returnUrl =
      typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : null
    if (returnUrl) {
      // Полная навигация (как в login/index.vue): returnUrl может вести на backend-страницу.
      window.location.href = returnUrl
      next(false)
      return
    }
    next('/analytics')
    return
  }

  if (!token) {
    next('/login')
    return
  }

  isAuthorized.value = true

  const decoded = jwtDecode<JwtPayload>(token)
  const userRoles = extractRoles(decoded)
  const allowedRoles = (to.meta.allowedRoles as Roles[] | undefined) ?? [Roles.Admin]

  // Сайдбар скрывает разделы по «старшей» роли пользователя, а не по роли конкретного роута.
  const staffRole =
    userRoles.find((role) => role === Roles.Admin) ??
    userRoles.find((role) => role === Roles.Manager) ??
    null

  if (userRoles.some((role) => allowedRoles.includes(role))) {
    currentUserRole.value = staffRole ?? userRoles[0] ?? null
    next()
    return
  }

  // Сотрудник без доступа к конкретному разделу (Manager на /staff) — на «Регистрации», не logout.
  if (staffRole && to.path !== '/registrations') {
    currentUserRole.value = staffRole
    next('/registrations')
    return
  }

  // Аутентифицирован, но роль не подходит и не сотрудник — на логин (не зависаем без next).
  isAuthorized.value = false
  void logout()
  next('/login')
})

const logout = async () => {
  await userAuthorizationService.logout()
}

// Мягкий выход: чистим токен, гасим фоновое обновление и SPA-редирект на логин — без
// localStorage.clear()+location.reload() (жёсткий перезаг «дёргал экран»).
emitter.on('logoutRequired', () => {
  jwtService.clear()
  httpClient.stopSession()
  isAuthorized.value = false
  if (router.currentRoute.value.path !== '/login') {
    void router.push('/login')
  }
})
</script>

<template>
  <AppLayout v-if="showLayout()" :role="currentUserRole" :on-logout="logout" />
  <RouterView v-else />
  <Toast />
  <ConfirmDialog />
</template>
